import React, { useEffect, useMemo, useRef, useState } from "react";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import logo from "assets/images/Dr_Hoffmann_Logo_f1.svg";
import LeonardoLanguageToggle from "components/LeonardoLanguageToggle";
import { useLeonardoLocale } from "scenes/leonardoNikolai/LeonardoLocaleContext";
import { setLogout } from "state";
import { formatPrice } from "constants/massages";
import { buildAuthHref } from "utils/registration";
import { queueAuthSuccessPopup } from "utils/authSuccessPopup";

const NAV_CURVE_LAYOUT = {
  home: { y: 15, angle: 2.8, slope: 0.26, bend: 0.08 },
  referral: { y: 28, angle: 1.2, slope: 0.12, bend: 0.05 },
  karriere: { y: 31, angle: 1.2, slope: 0.12, bend: 0.05 },
  kontakt: { y: 36, angle: 0.8, slope: 0.08, bend: 0.04 },
  admin: { y: 40, angle: 0.35, slope: 0.05, bend: 0.03 },
};

const CurvedNavLabel = ({ config }) => {
  const chars = Array.from(config.label);

  return (
    <span className="nav-curve-text" aria-hidden="true">
      {chars.map((char, index) => {
        return (
          <span
            className="nav-curve-char"
            key={`${config.label}-${index}-${char}`}
            style={{
              "--char-y": `${config.y}px`,
              "--char-angle": "0deg",
            }}
          >
            {char}
          </span>
        );
      })}
    </span>
  );
};

const BalanceTopUpLink = ({ user, className = "" }) => {
  if (!user) return null;
  const balanceCents = Math.max(0, Number(user.referralBalanceCents || 0));

  return (
    <Link
      to="/aufladen"
      className={`nav-balance-link ${className}`.trim()}
      aria-label={`Kontoguthaben ${formatPrice(balanceCents)} aufladen`}
    >
      <span className="nav-balance-copy">
        <span className="nav-balance-label">Guthaben</span>
        <strong>{formatPrice(balanceCents)}</strong>
      </span>
      <span className="nav-balance-plus" aria-hidden="true">
        <AddOutlinedIcon />
      </span>
    </Link>
  );
};

const Navbar = ({ renderNavLanguageToggle = null }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(() =>
    typeof window !== "undefined" ? window.scrollY > 24 : false
  );
  const accountMenuRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((s) => s.user);
  const { shellT, isLeonardoPage } = useLeonardoLocale();

  const navCurve = useMemo(
    () => ({
      home: { label: shellT("shell.nav.home"), ...NAV_CURVE_LAYOUT.home },
      referral: { label: shellT("shell.nav.referral"), ...NAV_CURVE_LAYOUT.referral },
      karriere: { label: shellT("shell.nav.career"), ...NAV_CURVE_LAYOUT.karriere },
      kontakt: { label: shellT("shell.nav.contact"), ...NAV_CURVE_LAYOUT.kontakt },
      admin: { label: shellT("shell.nav.admin"), ...NAV_CURVE_LAYOUT.admin },
    }),
    [shellT]
  );

  useEffect(() => {
    document.body.classList.toggle("menu-open", mobileOpen);
    return () => document.body.classList.remove("menu-open");
  }, [mobileOpen]);

  useEffect(() => {
    let frameId = null;

    const updateScrollState = () => {
      frameId = null;
      setIsScrolled(window.scrollY > 24);
    };

    const onScroll = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(updateScrollState);
    };

    updateScrollState();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!accountOpen) return undefined;

    const onPointerDown = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) {
        setAccountOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setAccountOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [accountOpen]);

  const closeMenu = () => setMobileOpen(false);
  const isHomePage = location.pathname === "/";
  const navClassName = [
    isScrolled ? "scrolled" : "",
    isHomePage && !isScrolled && !mobileOpen && !accountOpen ? "is-hidden-at-top" : "",
  ].filter(Boolean).join(" ");

  const logout = () => {
    dispatch(setLogout());
    setAccountOpen(false);
    queueAuthSuccessPopup("logout");
    navigate("/", { replace: true });
  };

  const mobileLogout = () => {
    dispatch(setLogout());
    setAccountOpen(false);
    setMobileOpen(false);
    queueAuthSuccessPopup("logout");
    navigate("/", { replace: true });
  };

  return (
    <>
      <nav className={navClassName}>
        {isLeonardoPage ? (
          <span className="nav-logo" aria-label="Dr. Hoffmann">
            <img src={logo} alt="Dr. Hoffmann" />
          </span>
        ) : (
          <Link to="/" className="nav-logo" aria-label="Dr. Hoffmann" onClick={closeMenu}>
            <img src={logo} alt="Dr. Hoffmann" />
          </Link>
        )}
        <ul className="nav-links">
          <li>
            <Link to="/empfehlungsprogramm" className="nav-curve-link nav-referral-link" aria-label={navCurve.referral.label}>
              <CurvedNavLabel config={navCurve.referral} />
            </Link>
          </li>
          <li>
            <Link to="/karriere" className="nav-curve-link" aria-label={navCurve.karriere.label}>
              <CurvedNavLabel config={navCurve.karriere} />
            </Link>
          </li>
          <li>
            <Link to="/kontakt" className="nav-curve-link" aria-label={navCurve.kontakt.label}>
              <CurvedNavLabel config={navCurve.kontakt} />
            </Link>
          </li>
          {user ? (
            user.isAdmin && (
              <li>
                <NavLink to="/admin-dashboard" className="nav-curve-link nav-admin-link" aria-label={navCurve.admin.label}>
                  <CurvedNavLabel config={navCurve.admin} />
                </NavLink>
              </li>
            )
          ) : null}
          <li className="nav-actions">
            {renderNavLanguageToggle?.() || (isLeonardoPage && <LeonardoLanguageToggle />)}
            <BalanceTopUpLink user={user} className="nav-balance-link--desktop" />
            <Link to="/buchen" className="nav-cta">{shellT("shell.nav.bookCta")}</Link>
            {user ? (
              <div className="nav-account-menu" ref={accountMenuRef}>
                <button
                  type="button"
                  className="nav-login-icon"
                  aria-label={shellT("shell.nav.accountMenu")}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  title={shellT("shell.nav.myAccount")}
                  onClick={() => setAccountOpen((open) => !open)}
                >
                  <AccountCircleOutlinedIcon aria-hidden="true" />
                </button>
                <div className={`nav-account-dropdown ${accountOpen ? "open" : ""}`} role="menu">
                  <Link to="/konto" role="menuitem" onClick={() => setAccountOpen(false)}>
                    <PersonOutlineOutlinedIcon aria-hidden="true" />
                    {shellT("shell.nav.myAccount")}
                  </Link>
                  <Link to="/konto/termine" role="menuitem" onClick={() => setAccountOpen(false)}>
                    <EventOutlinedIcon aria-hidden="true" />
                    {shellT("shell.nav.myAppointments")}
                  </Link>
                  <button type="button" role="menuitem" onClick={logout}>
                    <LogoutOutlinedIcon aria-hidden="true" />
                    {shellT("shell.nav.logout")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="nav-account-menu" ref={accountMenuRef}>
                <button
                  type="button"
                  className="nav-login-icon"
                  aria-label={shellT("shell.nav.loginMenu")}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  title={shellT("shell.nav.login")}
                  onClick={() => setAccountOpen((open) => !open)}
                >
                  <AccountCircleOutlinedIcon aria-hidden="true" />
                </button>
                <div className={`nav-account-dropdown ${accountOpen ? "open" : ""}`} role="menu">
                  <Link to={buildAuthHref("/einloggen")} role="menuitem" onClick={() => setAccountOpen(false)}>
                    <PersonOutlineOutlinedIcon aria-hidden="true" />
                    {shellT("shell.nav.login")}
                  </Link>
                  <Link to={buildAuthHref("/registrieren")} role="menuitem" onClick={() => setAccountOpen(false)}>
                    <AccountCircleOutlinedIcon aria-hidden="true" />
                    {shellT("shell.nav.register")}
                  </Link>
                </div>
              </div>
            )}
          </li>
        </ul>
        <div className="nav-mobile-controls">
          {renderNavLanguageToggle?.()}
          {isLeonardoPage && (
            <LeonardoLanguageToggle className="leonardo-lang-toggle--nav-mobile" />
          )}
          <BalanceTopUpLink user={user} className="nav-balance-link--mobile" />
          {!isLeonardoPage && (
            <div
              className={`hamburger ${mobileOpen ? "active" : ""}`}
              role="button"
              aria-label={shellT("shell.nav.openMenu")}
              aria-expanded={mobileOpen}
              tabIndex={0}
              onClick={() => setMobileOpen((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setMobileOpen((v) => !v);
                }
              }}
            >
              <span></span><span></span><span></span>
            </div>
          )}
        </div>
      </nav>

      <div className={`mobile-menu ${mobileOpen ? "open" : ""}`}>
        <Link to="/empfehlungsprogramm" onClick={closeMenu}>{shellT("shell.nav.referral")}</Link>
        <Link to="/karriere" onClick={closeMenu}>{shellT("shell.nav.career")}</Link>
        <Link to="/kontakt" onClick={closeMenu}>{shellT("shell.nav.contact")}</Link>
        {user ? (
          <>
            <Link to="/konto" onClick={closeMenu}>{shellT("shell.nav.myAccount")}</Link>
            <Link to="/konto/termine" onClick={closeMenu}>{shellT("shell.nav.myAppointments")}</Link>
            {user.isAdmin && (
              <Link to="/admin-dashboard" className="mobile-admin-link" onClick={closeMenu}>{shellT("shell.nav.admin")}</Link>
            )}
            <button type="button" className="mobile-menu-logout" onClick={mobileLogout}>
              Abmelden
            </button>
          </>
        ) : (
          <Link to={buildAuthHref("/einloggen")} onClick={closeMenu}>{shellT("shell.nav.login")}</Link>
        )}
        <Link to="/buchen" onClick={closeMenu} className="btn-primary">
          {shellT("shell.nav.bookCta")}
        </Link>
      </div>
    </>
  );
};

export default Navbar;
